'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

import { Button } from '@/shared/components/Button';
import { Input } from '@/shared/components/Input';
import { Label } from '@/shared/components/Label';
import { useAuth } from '@/features/auth';
import {
	loginSchema,
	type LoginFormData,
} from '@/features/auth/validation/schemas';
import { cn } from '@/shared/lib/utils';
import { logger } from '@/core/logger';

export interface LoginFormProps {
	className?: string;
}

export function LoginForm({ className }: LoginFormProps) {
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState<string>('');
	const { signIn } = useAuth();

	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting },
	} = useForm<LoginFormData>({
		resolver: zodResolver(loginSchema),
	});

	const onSubmit = async (data: LoginFormData) => {
		try {
			setError('');
			await signIn(data);
			logger.info('Login successful');
		} catch (error) {
			// Server action error already logged on server with appropriate level
			// Log user action outcome for client-side monitoring
			logger.warn('Login attempt failed');
			setError(
				error instanceof Error
					? error.message
					: 'An unexpected error occurred. Please try again.'
			);
		}
	};

	const isFormLoading = isSubmitting;

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className={cn('space-y-6', className)}
			noValidate
		>
			{error && (
				<div
					className="rounded-lg glass-light p-4 text-sm text-red-400 border border-red-400/30"
					role="alert"
					aria-live="polite"
				>
					{error}
				</div>
			)}

			<div className="space-y-3">
				<Label htmlFor="email" className="text-white/90">
					Email
				</Label>
				<Input
					id="email"
					type="email"
					autoComplete="email"
					placeholder="Enter your email"
					disabled={isFormLoading}
					aria-invalid={!!errors.email}
					aria-describedby={errors.email ? 'email-error' : undefined}
					className="glass-light border-white/20 text-white placeholder:text-white/40 focus:border-white/40 h-12"
					{...register('email')}
				/>
				{errors.email && (
					<p id="email-error" className="text-sm text-red-400">
						{errors.email.message}
					</p>
				)}
			</div>

			<div className="space-y-3">
				<div className="flex items-center justify-between">
					<Label htmlFor="password" className="text-white/90">
						Password
					</Label>
					<a
						href="/password/forgot"
						className="text-sm text-white/60 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-white/20 rounded px-1"
					>
						Forgot password?
					</a>
				</div>
				<div className="relative">
					<Input
						id="password"
						type={showPassword ? 'text' : 'password'}
						autoComplete="current-password"
						placeholder="Enter your password"
						disabled={isFormLoading}
						aria-invalid={!!errors.password}
						aria-describedby={
							errors.password ? 'password-error' : undefined
						}
						className="glass-light border-white/20 text-white placeholder:text-white/40 focus:border-white/40 pr-12 h-12"
						{...register('password')}
					/>
					<button
						type="button"
						className="absolute inset-y-0 right-0 flex items-center pr-4 text-white/60 hover:text-white transition-colors"
						onClick={() => setShowPassword(!showPassword)}
						disabled={isFormLoading}
						aria-label={
							showPassword ? 'Hide password' : 'Show password'
						}
					>
						{showPassword ? (
							<EyeOff className="h-5 w-5" />
						) : (
							<Eye className="h-5 w-5" />
						)}
					</button>
				</div>
				{errors.password && (
					<p id="password-error" className="text-sm text-red-400">
						{errors.password.message}
					</p>
				)}
			</div>

			<Button
				type="submit"
				className="w-full glass-frosted glass-hover bg-white/90 text-black font-semibold border-white/30 h-12 text-base"
				disabled={isFormLoading}
				aria-describedby={error ? 'form-error' : undefined}
			>
				{isFormLoading && (
					<Loader2 className="mr-2 h-5 w-5 animate-spin" />
				)}
				Sign in with email
			</Button>

			<div className="relative">
				<div className="absolute inset-0 flex items-center">
					<div className="w-full border-t border-white/10"></div>
				</div>
				<div className="relative flex justify-center text-sm">
					<span className="bg-transparent px-4 text-white/40">
						or
					</span>
				</div>
			</div>

			<div className="text-center pt-2">
				<span className="text-sm text-white/60">
					Don&apos;t have an account?{' '}
					<a
						href="/register"
						className="text-white font-medium hover:underline focus:outline-none focus:ring-2 focus:ring-white/20 rounded px-1"
					>
						Create a new account
					</a>
				</span>
			</div>
		</form>
	);
}
